import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useRecoilValueLoadable } from 'recoil';
import useFormatter from '../../common/useFormatter';
import { warehouseList, warehouseSelectedId } from '../../store/warehouse.state';
import { MetricGroupId } from '../store/actuality.types';
import { getActualityAggregationMetricDescriptorsMap } from '../store/feedMetrics';
// Import the proper aggregated stats selector
import { actualityAggregatedStatsTotal } from '../store/actuality.kpi.state';
import { ActualityAggregatedHqExtendedDataRow } from '../store/actuality.kpi.types';

// Enhanced interface for warehouse metrics with real actuality data
export interface WarehouseActualityMetrics {
  warehouseId: string;
  warehouseName: string;
  loadStatus: 'loading' | 'loaded' | 'error';
  
  // Raw actuality aggregated stats data
  aggregatedStats: ActualityAggregatedHqExtendedDataRow | null;
  
  // KPI Metrics organized by groups (calculated from aggregated stats)
  throughput: {
    totalJobs: number;
    totalOrders: number;
    totalLines: number;
    totalUnits: number;
    totalLocations: number;
  };
  
  performance: {
    netLinesPerHour: number;
    netUnitsPerHour: number;
    netJobsPerHour: number;
    netOrdersPerHour: number;
    netLocationsPerHour: number;
  };
  
  efficiency: {
    netWorkedTimeMs: number;
    grossTimeMs: number;
    idleTimeMs: number;
    efficiencyRatio: number; // net time / gross time
    utilizationRatio: number; // (net time + idle time) / gross time
  };
  
  labor: {
    totalWorkedHours: number;
    totalIdleHours: number;
    totalGrossHours: number;
    avgWorkedTimePerJob: number;
    laborProductivity: number;
  };
  
  process: {
    avgLinesPerJob: number;
    avgUnitsPerLine: number;
    avgLocationsPerJob: number;
    avgTimePerJob: number;
    processVariability: number;
  };
  
  // Additional calculated metrics for comparison
  aggregated: {
    [key: string]: number;
  };
}

export interface KPIGroupConfig {
  id: MetricGroupId;
  title: string;
  description: string;
  metrics: Array<{
    key: string;
    title: string;
    format: 'integer' | 'decimal' | 'percentage' | 'duration' | 'currency';
    unit?: string;
  }>;
}

export function useActualityWarehouseComparison() {
  const { t } = useTranslation('feed');
  const formatter = useFormatter();
  const warehouses = useRecoilValue(warehouseList);
  const selectedWarehouseId = useRecoilValue(warehouseSelectedId);
  const metricDescriptors = getActualityAggregationMetricDescriptorsMap(t, formatter);
  
  // Use the aggregated stats selector that has all calculated KPIs
  const aggregatedStatsLoadable = useRecoilValueLoadable(actualityAggregatedStatsTotal);
  
  const [warehouseMetrics, setWarehouseMetrics] = useState<Record<string, WarehouseActualityMetrics>>({});

  // Define KPI group configurations
  const kpiGroups: KPIGroupConfig[] = [
    {
      id: 'velocity',
      title: t('Velocity Metrics'),
      description: t('Speed and efficiency metrics for movement and operations'),
      metrics: [
        { key: 'netLinesPerformance', title: t('Lines/Hour'), format: 'decimal', unit: 'lines/hr' },
        { key: 'netUomsPerformance', title: t('Units/Hour'), format: 'decimal', unit: 'units/hr' },
        { key: 'netJobsPerformance', title: t('Jobs/Hour'), format: 'decimal', unit: 'jobs/hr' },
        { key: 'avgTimePerJob', title: t('Avg Time/Job'), format: 'duration', unit: 'min' },
      ]
    },
    {
      id: 'process',
      title: t('Process Analysis'),
      description: t('Process efficiency and workflow metrics'),
      metrics: [
        { key: 'jobLineRatio', title: t('Lines/Job'), format: 'decimal' },
        { key: 'uomLineRatio', title: t('Units/Line'), format: 'decimal' },
        { key: 'locationsPerJob', title: t('Locations/Job'), format: 'decimal' },
        { key: 'netGrossRatio', title: t('Efficiency Ratio'), format: 'percentage', unit: '%' },
      ]
    },
    {
      id: 'time',
      title: t('Time Analysis'),
      description: t('Time utilization and efficiency metrics'),
      metrics: [
        { key: 'netGrossRatio', title: t('Efficiency Ratio'), format: 'percentage', unit: '%' },
        { key: 'idleNetRatio', title: t('Idle Ratio'), format: 'percentage', unit: '%' },
        { key: 'totalWorkedHours', title: t('Total Worked Hours'), format: 'decimal', unit: 'hrs' },
        { key: 'totalIdleHours', title: t('Total Idle Hours'), format: 'decimal', unit: 'hrs' },
      ]
    },
    {
      id: 'inventory',
      title: t('Throughput Metrics'),
      description: t('Volume and quantity processed'),
      metrics: [
        { key: 'jobsCount', title: t('Total Jobs'), format: 'integer' },
        { key: 'jobGroupsCount', title: t('Total Orders'), format: 'integer' },
        { key: 'eventsCount', title: t('Total Lines'), format: 'integer' },
        { key: 'uomCount', title: t('Total Units'), format: 'integer' },
      ]
    }
  ];

  // Calculate metrics from aggregated stats
  const calculateMetricsFromAggregatedStats = useCallback((
    stats: ActualityAggregatedHqExtendedDataRow | null
  ): Omit<WarehouseActualityMetrics, 'warehouseId' | 'warehouseName' | 'loadStatus' | 'aggregatedStats'> => {
    if (!stats) {
      return {
        throughput: { totalJobs: 0, totalOrders: 0, totalLines: 0, totalUnits: 0, totalLocations: 0 },
        performance: { netLinesPerHour: 0, netUnitsPerHour: 0, netJobsPerHour: 0, netOrdersPerHour: 0, netLocationsPerHour: 0 },
        efficiency: { netWorkedTimeMs: 0, grossTimeMs: 0, idleTimeMs: 0, efficiencyRatio: 0, utilizationRatio: 0 },
        labor: { totalWorkedHours: 0, totalIdleHours: 0, totalGrossHours: 0, avgWorkedTimePerJob: 0, laborProductivity: 0 },
        process: { avgLinesPerJob: 0, avgUnitsPerLine: 0, avgLocationsPerJob: 0, avgTimePerJob: 0, processVariability: 0 },
        aggregated: {}
      };
    }

    // Extract values from the aggregated stats (these are already calculated!)
    const netWorkedTimeMs = stats.totalEmployeeActivityDuration || 0;
    const grossTimeMs = stats.grossTimeMs || 0;
    const idleTimeMs = stats.idleTimeMs || 0;
    
    const netWorkedHours = netWorkedTimeMs / (1000 * 60 * 60);
    const grossHours = grossTimeMs / (1000 * 60 * 60);
    const idleHours = idleTimeMs / (1000 * 60 * 60);

    // Calculate average time per job in minutes
    const avgTimePerJob = stats.jobsCount > 0 ? netWorkedTimeMs / stats.jobsCount / (1000 * 60) : 0;

    return {
      throughput: {
        totalJobs: stats.jobsCount || 0,
        totalOrders: stats.jobGroupsCount || 0,
        totalLines: stats.eventsCount || 0,
        totalUnits: stats.uomCount || 0,
        totalLocations: stats.uniqLocationsCount || 0,
      },
      performance: {
        netLinesPerHour: stats.netLinesPerformance || 0,
        netUnitsPerHour: stats.netUomsPerformance || 0,
        netJobsPerHour: stats.netJobsPerformance || 0,
        netOrdersPerHour: stats.netOrdersPerformance || 0,
        netLocationsPerHour: stats.netLocationsPerformance || 0,
      },
      efficiency: {
        netWorkedTimeMs,
        grossTimeMs,
        idleTimeMs,
        efficiencyRatio: (stats.netGrossRatio || 0) * 100, // Convert to percentage
        utilizationRatio: grossTimeMs > 0 ? ((netWorkedTimeMs + idleTimeMs) / grossTimeMs) * 100 : 0,
      },
      labor: {
        totalWorkedHours: netWorkedHours,
        totalIdleHours: idleHours,
        totalGrossHours: grossHours,
        avgWorkedTimePerJob: avgTimePerJob,
        laborProductivity: stats.netUomsPerformance || 0,
      },
      process: {
        avgLinesPerJob: stats.jobLineRatio || 0,
        avgUnitsPerLine: stats.uomLineRatio || 0,
        avgLocationsPerJob: stats.locationsPerJob || 0,
        avgTimePerJob,
        processVariability: 0, // Could be calculated from additional data if needed
      },
      aggregated: {
        totalVolume: stats.uomCount || 0,
        avgProductivity: stats.netLinesPerformance || 0,
        efficiencyScore: (stats.netGrossRatio || 0) * 100,
        utilizationScore: grossTimeMs > 0 ? ((netWorkedTimeMs) / grossTimeMs) * 100 : 0,
      }
    };
  }, []);

  // Update warehouse metrics when aggregated stats change for current warehouse
  useEffect(() => {
    if (!selectedWarehouseId) return;

    const warehouse = warehouses?.find(wh => wh.id === selectedWarehouseId);
    if (!warehouse) return;

    const aggregatedStats = aggregatedStatsLoadable.state === 'hasValue' ? aggregatedStatsLoadable.getValue() : null;
    const loadStatus = aggregatedStatsLoadable.state === 'loading' ? 'loading' :
                      aggregatedStatsLoadable.state === 'hasError' ? 'error' : 'loaded';

    const calculatedMetrics = calculateMetricsFromAggregatedStats(aggregatedStats);

    setWarehouseMetrics(prev => ({
      ...prev,
      [selectedWarehouseId]: {
        warehouseId: selectedWarehouseId,
        warehouseName: warehouse.title,
        loadStatus,
        aggregatedStats,
        ...calculatedMetrics
      }
    }));
  }, [
    selectedWarehouseId, 
    warehouses, 
    aggregatedStatsLoadable,
    calculateMetricsFromAggregatedStats
  ]);

  // Load data for multiple warehouses (placeholder for future expansion)
  const loadWarehousesData = useCallback((warehouseIds: string[]) => {
    // For multi-warehouse comparison, we'd need warehouse-specific selectors
    // For now, we'll create placeholder entries for other warehouses
    warehouseIds.forEach(warehouseId => {
      if (!warehouseMetrics[warehouseId] && warehouseId !== selectedWarehouseId) {
        const warehouse = warehouses?.find(wh => wh.id === warehouseId);
        if (warehouse) {
          setWarehouseMetrics(prev => ({
            ...prev,
            [warehouseId]: {
              warehouseId,
              warehouseName: warehouse.title,
              loadStatus: 'loaded',
              aggregatedStats: null,
              ...calculateMetricsFromAggregatedStats(null)
            }
          }));
        }
      }
    });
  }, [warehouseMetrics, selectedWarehouseId, warehouses, calculateMetricsFromAggregatedStats]);

  // Get metrics for comparison charts
  const getComparisonData = useCallback((
    warehouseIds: string[], 
    kpiGroupId: MetricGroupId, 
    metricKey: string
  ) => {
    const kpiGroup = kpiGroups.find(group => group.id === kpiGroupId);
    const metric = kpiGroup?.metrics.find(m => m.key === metricKey);
    
    if (!metric || !warehouseIds.length) return [];

    return warehouseIds
      .map(warehouseId => {
        const metrics = warehouseMetrics[warehouseId];
        if (!metrics) return null;

        // Get value directly from aggregated stats or calculated metrics
        let value = 0;

        // First try to get from aggregated stats directly
        if (metrics.aggregatedStats && metrics.aggregatedStats[metricKey] !== undefined) {
          value = metrics.aggregatedStats[metricKey] as number;
        } else {
          // Navigate through nested object structure to get value
          const getValue = (obj: any, path: string): number => {
            const keys = path.split('.');
            let val = obj;
            for (const key of keys) {
              val = val?.[key];
              if (val === undefined) return 0;
            }
            return typeof val === 'number' ? val : 0;
          };

          // Try to find the value in different metric categories
          for (const category of ['throughput', 'performance', 'efficiency', 'labor', 'process', 'aggregated']) {
            const categoryValue = getValue(metrics[category], metricKey.replace(`${category}.`, ''));
            if (categoryValue > 0) {
              value = categoryValue;
              break;
            }
          }
        }

        return {
          warehouseId,
          warehouseName: metrics.warehouseName,
          value,
          metricKey,
          format: metric.format,
          unit: metric.unit || '',
        };
      })
      .filter(Boolean);
  }, [warehouseMetrics, kpiGroups]);

  // Format value based on metric format
  const formatMetricValue = useCallback((value: number, format: string, unit?: string) => {
    switch (format) {
      case 'integer':
        return Math.round(value).toLocaleString();
      case 'decimal':
        return value.toFixed(1);
      case 'percentage':
        return `${Math.round(value)}%`;
      case 'duration':
        return `${Math.round(value)}${unit || 'min'}`;
      case 'currency':
        return formatter.formatCurrency(value).fullString;
      default:
        return value.toString();
    }
  }, [formatter]);

  const isLoading = aggregatedStatsLoadable.state === 'loading';

  return {
    warehouseMetrics,
    kpiGroups,
    loadWarehousesData,
    fetchWarehouseData: loadWarehousesData, // alias for compatibility
    getComparisonData,
    formatMetricValue,
    isLoading,
    availableWarehouses: warehouses || [],
  };
} 